// Build a WeatherLayers GL VECTOR TextureData from decoded Skysight uv tiles.
// WeatherLayers expects U in R, V in G, A=0 for no-data, values quantized over
// `imageUnscale: [min, max]`, and an image that maps LINEARLY IN LATITUDE
// (equirectangular) onto `bounds: [west, south, east, north]`. Skysight tiles
// are Web-Mercator, so rows are RESAMPLED (mercator → equirect) here; columns
// are linear in longitude in both projections and map 1:1.

import { decodeUV } from './decode';

const TILE = 256; // use each tile's first 256×256 (drop the 1px overlap)

const mercYFromLat = (lat) => (1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2;
const latFromMercY = (y) => (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
const lngFromTileX = (x, n) => (x / n) * 360 - 180;

// getTile(x, y) -> { w, h, data } (decoded RGBA, tileCache shape) or null.
// range = the product's uv dequantization range ({ min: [u,v], max: [u,v] }).
// Returns { data, width, height, bounds, imageUnscale } for ParticleLayer.
export function buildParticleImage({ getTile, z, xMin, xMax, yMin, yMax, range, maskAbove = null }) {
  const n = Math.pow(2, z);
  const cols = xMax - xMin + 1;
  const rows = yMax - yMin + 1;
  const width = cols * TILE;
  const height = rows * TILE;
  const west = lngFromTileX(xMin, n);
  const east = lngFromTileX(xMax + 1, n);
  const north = latFromMercY(yMin / n);
  const south = latFromMercY((yMax + 1) / n);
  const uMin = range.min[0];
  const uMax = range.max[0];
  const vMin = range.min[1];
  const vMax = range.max[1];
  const data = new Uint8ClampedArray(width * height * 4);

  for (let i = 0; i < height; i++) {
    // Output row at a latitude linear between north and south; locate the
    // mercator source row it corresponds to.
    const lat = north + ((south - north) * (i + 0.5)) / height;
    const ty = mercYFromLat(lat) * n;
    const tileY = Math.max(yMin, Math.min(yMax, Math.floor(ty)));
    const py = Math.max(0, Math.min(TILE - 1, Math.floor((ty - tileY) * TILE)));
    for (let cx = 0; cx < cols; cx++) {
      const tile = getTile(xMin + cx, tileY);
      const rowBase = (i * width + cx * TILE) * 4;
      if (!tile) continue; // missing tile → A stays 0 (no particles)
      const srcBase = py * tile.w * 4;
      for (let px = 0; px < TILE; px++) {
        const si = srcBase + px * 4;
        const uv = decodeUV(
          tile.data[si] / 255, tile.data[si + 1] / 255, tile.data[si + 2] / 255, tile.data[si + 3] / 255,
          range.min, range.max,
        );
        if (!uv) continue;
        if (maskAbove) {
          const lng = west + ((east - west) * (cx * TILE + px + 0.5)) / width;
          if (maskAbove(lng, lat)) continue; // above terrain → no particle
        }
        const di = rowBase + px * 4;
        data[di] = Math.round(((uv.u - uMin) / (uMax - uMin)) * 255);
        data[di + 1] = Math.round(((uv.v - vMin) / (vMax - vMin)) * 255);
        data[di + 3] = 255;
      }
    }
  }

  return {
    data,
    width,
    height,
    bounds: [west, south, east, north],
    imageUnscale: [uMin, uMax],
  };
}
